import {
    DASH_DEFAULT,
    NA_DEFAULT,
    displayShortDate
} from '../../utils/Format/Format';
import {
    escapeCommas,
    getCurrencyFormatter,
    getDateFormatter,
    getNumberFormatter,
    getPercentFormatter
} from './agGridUtils';

// This function checks for null, undefined, or empty string values and returns
// a default value if any of those conditions are met.
const getValue = (val, defaultVal = DASH_DEFAULT) =>
    val === null || val === undefined || val === '' ? defaultVal : val;

// This function takes in an array of holdings and an asset type name and returns
// an object with two properties: agData and csvData. agData is an array of AgGrid
// row data and csvData is an array of CSV row data.
const dataBuilder = (holdings, isFixedIncome, isMonthly, asOfDate) => {
    const agData = [];
    const csvData = [];

    for (let i = 0; i < holdings.length; i++) {
        const {
            name,
            ticker,
            shareQuantity,
            marketValue,
            percentTotalNetAssets,
            holdingCouponRate,
            holdingMaturityDate,
            investmentType,
            strikePrice,
            prioritizedIdentifier,
            issueDate,
            industryName
        } = holdings[i];

        // This is to filter out US DOLLAR holdings, I really do think
        // that skipping the loop iteration is easier to maintain than
        // adding a giant if block around the whole thing in this case.
        // eslint-disable-next-line no-continue
        if (industryName && industryName.toUpperCase() === 'CASH') continue;

        const finalName = getValue(name);
        const finalTicker = getValue(ticker);
        const finalShareQuantity = getValue(shareQuantity);
        const finalMarketValue = getValue(marketValue);
        const finalPercentTotalNetAssets = getValue(percentTotalNetAssets);
        const finalCouponRate = getValue(holdingCouponRate);
        const finalHoldingMaturityDate = getValue(holdingMaturityDate);
        const finalInvestmentType = getValue(investmentType);
        const finalStrikePrice = getValue(strikePrice);
        const finalPrioritizedIdentifier = getValue(
            prioritizedIdentifier,
            NA_DEFAULT
        );
        const finalIssueDate = getValue(issueDate, NA_DEFAULT);

        const dataItem = {
            name: finalName,
            ticker: finalTicker,
            shareQuantity: finalShareQuantity,
            marketValue: finalMarketValue,
            percentTotalNetAssets: finalPercentTotalNetAssets,
            holdingCouponRate: finalCouponRate,
            holdingMaturityDate: finalHoldingMaturityDate,
            investmentType: finalInvestmentType,
            strikePrice: finalStrikePrice,
            prioritizedIdentifier: finalPrioritizedIdentifier,
            issueDate: finalIssueDate
        };

        const csvCoreData = {
            'As of Date': displayShortDate(asOfDate),
            Name: escapeCommas(finalName),
            '% of Fund': getPercentFormatter({
                val: finalPercentTotalNetAssets
            })(),
            'Security / Asset Quantity': getNumberFormatter({
                val: finalShareQuantity,
                isCsv: true,
                showDecimals: !isMonthly
            })(),
            'Market Value': getCurrencyFormatter({
                val: finalMarketValue,
                isCsv: true
            })()
        };

        let csvDailyData;

        if (!isMonthly) {
            csvDailyData = {
                ...csvCoreData,
                Ticker: escapeCommas(finalTicker),
                Identifier: escapeCommas(finalPrioritizedIdentifier),
                'Investment Type': escapeCommas(finalInvestmentType),
                'Options Strike Price': getCurrencyFormatter({
                    val: finalStrikePrice,
                    isCsv: true
                })()
            };
        } else {
            csvDailyData = {
                ...csvCoreData
            };
        }

        let csvFixedIncomeData;

        if (isMonthly || isFixedIncome) {
            csvFixedIncomeData = {
                ...csvDailyData,
                Maturity: getDateFormatter({
                    val: finalHoldingMaturityDate
                })(),
                'Coupon %': getPercentFormatter({
                    val: finalCouponRate
                })()
            };
        } else {
            csvFixedIncomeData = { ...csvDailyData };
        }

        let csvDataItem;

        if (isFixedIncome && !isMonthly) {
            csvDataItem = {
                ...csvFixedIncomeData,
                'Issue Date': getDateFormatter({ val: finalIssueDate })()
            };
        } else {
            csvDataItem = { ...csvFixedIncomeData };
        }

        agData.push(dataItem);

        csvData.push(csvDataItem);
    }

    return {
        agData,
        csvData
    };
};

export default dataBuilder;
